/** Erreur HTTP normalisée : le corps `problem+json` du backend est exposé tel quel. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly body: unknown;
  /**
   * `Retry-After` de la réponse, en secondes (forme « secondes » ou « date HTTP »), quand le
   * serveur en pose un : 503 `DATABASE_BUSY` / 504 `DATABASE_TIMEOUT` depuis 2eb44025.
   */
  readonly retryAfterSeconds: number | undefined;
  /**
   * Identifiant de corrélation de la requête côté serveur, pour la référence montrée à
   * l'utilisateur : champ facultatif `traceId` du corps `Error` (`common-v100.yaml`), sinon en-tête
   * `X-Request-Id` de la réponse. `undefined` tant que l'API ne pose ni l'un ni l'autre.
   */
  readonly traceId: string | undefined;

  /**
   * `requestId` : valeur de l'en-tête `X-Request-Id`, quand l'appelant a la réponse sous la main.
   * Le `traceId` du corps l'emporte : c'est celui que le serveur a journalisé avec l'erreur.
   */
  constructor(
    status: number,
    body: unknown,
    message?: string,
    retryAfterSeconds?: number,
    requestId?: string | null,
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.retryAfterSeconds = retryAfterSeconds;
    this.traceId = bodyTraceId(body) ?? (requestId?.trim() || undefined);
    this.code =
      typeof body === 'object' &&
      body !== null &&
      'code' in body &&
      typeof (body as { code: unknown }).code === 'string'
        ? (body as { code: string }).code
        : undefined;
  }

  /** Base momentanément occupée ou lente : 503 / 504, à réessayer (l'inverse d'un 501). */
  get isBusy(): boolean {
    return this.status === 503 || this.status === 504;
  }

  /** Une fonction coupée répond 501 `feature_disabled` (règle 2 du CLAUDE.md), jamais 404. */
  get isFeatureDisabled(): boolean {
    return this.status === 501 && this.code === 'feature_disabled';
  }
}

function bodyTraceId(body: unknown): string | undefined {
  if (typeof body !== 'object' || body === null || !('traceId' in body)) return undefined;
  const value = (body as { traceId: unknown }).traceId;
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

/** `Retry-After` → secondes (≥ 0, entières) ; `undefined` si absent ou illisible. */
export function parseRetryAfter(
  value: string | null | undefined,
  now: number = Date.now(),
): number | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (/^\d+$/u.test(trimmed)) return Number(trimmed);
  const date = Date.parse(trimmed);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.ceil((date - now) / 1000));
}
